/**
 * Formatting utilities for displaying data
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(duration);

/**
 * Format a date string to a readable format
 */
export function formatDate(date: string | Date | null | undefined, format = 'MMM D, YYYY'): string {
  if (!date) return '-';
  return dayjs(date).format(format);
}

/**
 * Format a date with time
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return dayjs(date).format('MMM D, YYYY h:mm A');
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return dayjs(date).fromNow();
}

/**
 * Format a timestamp for logs (HH:mm:ss)
 */
export function formatTimestamp(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return dayjs(date).format('HH:mm:ss');
}

/**
 * Format duration in milliseconds to readable string
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return '-';

  const d = dayjs.duration(ms);

  if (ms < 60000) {
    return `${Math.round(ms / 1000)}s`;
  }

  if (ms < 3600000) {
    const mins = Math.floor(d.asMinutes());
    const secs = d.seconds();
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }

  const hours = Math.floor(d.asHours());
  const mins = d.minutes();
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format estimated time remaining
 */
export function formatTimeRemaining(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return 'Calculating...';
  if (ms === 0) return 'Almost done';

  return `~${formatDuration(ms)}`;
}

/**
 * Format a number with thousands separator
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString();
}

/**
 * Format a compact number (e.g., 1.2K, 3.4M)
 */
export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';

  if (value < 1000) return value.toString();
  if (value < 1000000) return `${(value / 1000).toFixed(1)}K`;
  if (value < 1000000000) return `${(value / 1000000).toFixed(1)}M`;
  return `${(value / 1000000000).toFixed(1)}B`;
}

/**
 * Format a percentage
 */
export function formatPercent(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a rating (e.g., 4.5)
 */
export function formatRating(rating: number | null | undefined): string {
  if (rating === null || rating === undefined) return '-';
  return rating.toFixed(1);
}

/**
 * Format coordinates
 */
export function formatCoordinates(lat: number | null | undefined, lng: number | null | undefined): string {
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return '-';
  }
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
}

/**
 * Format area in square kilometers
 */
export function formatArea(areaKm2: number | null | undefined): string {
  if (areaKm2 === null || areaKm2 === undefined) return '-';
  if (areaKm2 < 1) return `${(areaKm2 * 1000000).toFixed(0)} m²`;
  return `${areaKm2.toFixed(1)} km²`;
}

/**
 * Format phone number (basic formatting)
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '-';
  return phone;
}

/**
 * Format website URL (remove protocol, truncate if needed)
 */
export function formatWebsite(url: string | null | undefined, maxLength = 30): string {
  if (!url) return '-';

  let formatted = url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  if (formatted.length > maxLength) {
    formatted = `${formatted.substring(0, maxLength - 3)}...`;
  }

  return formatted;
}

/**
 * Format bytes to human readable size
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength - 3)}...`;
}

/**
 * Format job progress as string
 */
export function formatProgress(completed: number, total: number): string {
  if (total === 0) return '0%';
  const percent = Math.round((completed / total) * 100);
  return `${percent}%`;
}

/**
 * Format job progress details
 */
export function formatProgressDetails(completed: number, total: number): string {
  return `${formatNumber(completed)} / ${formatNumber(total)}`;
}

/**
 * Format places per minute rate
 */
export function formatRate(placesPerMinute: number | null | undefined): string {
  if (placesPerMinute === null || placesPerMinute === undefined) return '-';
  return `${placesPerMinute.toFixed(0)}/min`;
}

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 */
export function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
